import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Swords, Loader2, ChevronRight, Trophy, RotateCcw, ThumbsUp, Minus } from 'lucide-react';
import { getRoomTheme } from '@/config/roomThemes';

const API = process.env.REACT_APP_BACKEND_URL;
const ROUND_LABELS = { opening: 'Opening Statements', rebuttal: 'Rebuttals', closing: 'Closing Arguments' };
const ROUND_ORDER = ['opening', 'rebuttal', 'closing'];

const DebateStage = ({ debate, mind1, mind2, room, onClose }) => {
  const [rounds, setRounds] = useState([]);
  const [currentRoundIdx, setCurrentRoundIdx] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [votes, setVotes] = useState([]); // Array of 'mind1' | 'mind2' | 'draw'
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const theme = getRoomTheme(room?.id) || { accent: '#B8860B' };

  const needsVote = rounds.length > 0 && votes.length < rounds.length;
  const score1 = votes.filter(v => v === 'mind1').length;
  const score2 = votes.filter(v => v === 'mind2').length;
  const draws = votes.filter(v => v === 'draw').length;

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 300);
    }
  }, [rounds, votes, generating]);

  const generateRound = async () => {
    if (generating || currentRoundIdx >= ROUND_ORDER.length || needsVote) return;
    setGenerating(true);
    setError(null);
    const roundName = ROUND_ORDER[currentRoundIdx];
    try {
      const res = await fetch(`${API}/api/debates/${debate.debate_id}/generate-round?round_name=${roundName}`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setRounds(prev => [...prev, { name: roundName, label: ROUND_LABELS[roundName], mind1_content: data.mind1_content, mind2_content: data.mind2_content }]);
      if (!data.next_round || data.next_round === 'completed') {
        // Last round — vote needed before showing results
      } else {
        setCurrentRoundIdx(prev => prev + 1);
      }
    } catch { setError('Failed to generate round. Please try again.'); }
    finally { setGenerating(false); }
  };

  const castVote = (vote) => {
    setVotes(prev => [...prev, vote]);
    // Check if this was the final round
    if (rounds.length === ROUND_ORDER.length && votes.length + 1 === ROUND_ORDER.length) {
      setTimeout(() => setCompleted(true), 800);
    }
  };

  // Auto-start first round
  useEffect(() => { if (rounds.length === 0 && !generating) generateRound(); }, []); // eslint-disable-line

  const getWinner = () => {
    if (score1 > score2) return { name: mind1.name, portrait: mind1.portrait, label: 'Proposer Wins' };
    if (score2 > score1) return { name: mind2.name, portrait: mind2.portrait, label: 'Opposition Wins' };
    return { name: 'Draw', portrait: null, label: 'Honourable Draw' };
  };

  return (
    <div data-testid="debate-stage" className="fixed inset-0 z-50 flex flex-col overflow-hidden" style={{ background: '#F5F7F6' }}>
      <div className="paper-texture" />

      {/* Oxford Chamber Header */}
      <div style={{ background: 'linear-gradient(135deg, #2F3E46 0%, #354F52 100%)', borderBottom: '3px solid #B8860B' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <button data-testid="debate-back-btn" onClick={onClose} className="flex items-center gap-2 font-ui text-xs tracking-wide cursor-pointer text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Exit Chamber</span>
          </button>
          <h1 className="font-display text-sm sm:text-base tracking-wider text-white flex items-center gap-2">
            <Swords className="w-4 h-4" /> The Oxford Chamber
          </h1>
          <div className="w-20" />
        </div>
      </div>

      {/* Debaters + Live Score Bar */}
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-8 py-3" style={{ borderBottom: '1px solid #E6E1DB' }}>
        <div className="flex items-center justify-between gap-3">
          <DebaterBadge mind={mind1} label="Proposer" score={score1} align="left" />
          <div className="flex-1 min-w-0 px-2">
            {/* Live Score Bar */}
            <div className="mb-1.5">
              <div className="h-2 rounded-full overflow-hidden flex" style={{ background: '#E6E1DB' }}>
                {votes.length > 0 && (
                  <>
                    <motion.div className="h-full" style={{ background: '#2F3E46' }}
                      initial={{ width: 0 }} animate={{ width: `${(score1 / votes.length) * 100}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                    <motion.div className="h-full" style={{ background: '#B8860B' }}
                      initial={{ width: 0 }} animate={{ width: `${(draws / votes.length) * 100}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                    <motion.div className="h-full" style={{ background: '#8B2252' }}
                      initial={{ width: 0 }} animate={{ width: `${(score2 / votes.length) * 100}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                  </>
                )}
              </div>
            </div>
            <p className="font-body text-[11px] sm:text-xs italic text-center truncate" style={{ color: '#8C8680' }}>
              &ldquo;{debate.topic}&rdquo;
            </p>
          </div>
          <DebaterBadge mind={mind2} label="Opposition" score={score2} align="right" />
        </div>
      </div>

      {/* Round Progress Dots */}
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-8 py-2.5">
        <div className="flex items-center gap-2 justify-center">
          {ROUND_ORDER.map((r, i) => {
            const done = i < rounds.length;
            const voted = i < votes.length;
            const active = i === currentRoundIdx && generating;
            return (
              <React.Fragment key={r}>
                {i > 0 && <div className="w-6 h-px" style={{ background: done ? '#B8860B' : '#E6E1DB' }} />}
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full font-ui text-[9px] tracking-wider uppercase"
                  style={{
                    background: voted ? '#B8860B' : done ? 'rgba(184,134,11,0.1)' : 'transparent',
                    color: voted ? '#fff' : done ? '#B8860B' : active ? '#B8860B' : '#8C8680',
                    border: `1px solid ${done ? '#B8860B' : active ? '#B8860B' : '#E6E1DB'}`,
                  }}>
                  <span>{i + 1}</span>
                  <span className="hidden sm:inline">{ROUND_LABELS[r].split(' ')[0]}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Debate Content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-28">
          {rounds.map((round, ri) => (
            <div key={`${round.name}-${ri}`} className="mb-6">
              {/* Round label */}
              <motion.div className="text-center mb-5 mt-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <span className="inline-block px-4 py-1.5 rounded-full font-ui text-[9px] tracking-[0.2em] uppercase"
                  style={{ background: '#F2EFE9', color: '#8C8680', border: '1px solid #E6E1DB' }}>
                  Round {ri + 1} &mdash; {round.label}
                </span>
              </motion.div>

              {/* Split speeches */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                <SpeechCard mind={mind1} content={round.mind1_content} label="Proposer" delay={0.15} color="#2F3E46" />
                <SpeechCard mind={mind2} content={round.mind2_content} label="Opposition" delay={0.4} color="#8B2252" />
              </div>

              {/* Vote section — only show after speeches, if not yet voted */}
              {ri < votes.length ? (
                <motion.div className="text-center mt-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <span className="inline-block px-3 py-1 rounded-full font-ui text-[9px] tracking-wider uppercase"
                    style={{
                      background: votes[ri] === 'mind1' ? '#2F3E46' : votes[ri] === 'mind2' ? '#8B2252' : '#B8860B',
                      color: '#fff',
                    }}>
                    {votes[ri] === 'mind1' ? `${mind1.name} wins round` : votes[ri] === 'mind2' ? `${mind2.name} wins round` : 'Round drawn'}
                  </span>
                </motion.div>
              ) : ri === votes.length ? (
                <VoteCard mind1={mind1} mind2={mind2} onVote={castVote} roundNum={ri + 1} />
              ) : null}
            </div>
          ))}

          {/* Generating */}
          {generating && (
            <motion.div className="text-center py-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" style={{ color: '#B8860B' }} />
              <p className="font-body text-sm" style={{ color: '#8C8680' }}>
                {rounds.length === 0 ? 'The debaters prepare their opening statements...' : 'Formulating responses...'}
              </p>
            </motion.div>
          )}

          {error && (
            <div className="text-center py-4">
              <p className="text-xs mb-2" style={{ color: '#C0392B' }}>{error}</p>
              <button onClick={generateRound} className="px-4 py-2 rounded font-ui text-xs cursor-pointer" style={{ background: '#1A1512', color: '#fff' }}>
                <RotateCcw className="w-3 h-3 inline mr-1" /> Retry
              </button>
            </div>
          )}

          {/* Final Results */}
          <AnimatePresence>
            {completed && <VerdictCard mind1={mind1} mind2={mind2} score1={score1} score2={score2} draws={draws} winner={getWinner()} onClose={onClose} />}
          </AnimatePresence>
        </div>
      </div>

      {/* Next Round CTA — only after voting */}
      {!completed && !generating && !needsVote && rounds.length > 0 && rounds.length < ROUND_ORDER.length && (
        <motion.div className="fixed bottom-0 left-0 right-0 p-4 flex justify-center z-10"
          style={{ background: 'linear-gradient(to top, #F5F7F6 60%, transparent)' }}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button data-testid="next-round-btn" onClick={generateRound}
            className="px-8 py-3 font-display text-xs tracking-[0.2em] uppercase flex items-center gap-2 cursor-pointer transition-all"
            style={{ background: '#1A1512', color: '#FDFCFB', boxShadow: '0 8px 24px rgba(26,21,18,0.2)' }}>
            Next: {ROUND_LABELS[ROUND_ORDER[currentRoundIdx]]}
            <ChevronRight className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </div>
  );
};

/* ── Vote Card ── */
const VoteCard = ({ mind1, mind2, onVote, roundNum }) => (
  <motion.div
    data-testid={`vote-card-round-${roundNum}`}
    className="mt-5 p-4 sm:p-5 rounded-xl text-center"
    style={{ background: 'white', border: '2px solid #B8860B30', boxShadow: '0 8px 24px rgba(184,134,11,0.08)' }}
    initial={{ opacity: 0, y: 15, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ type: 'spring', stiffness: 200, damping: 20 }}
  >
    <p className="font-display text-sm tracking-wider mb-4" style={{ color: '#1A1512' }}>
      Who won Round {roundNum}?
    </p>
    <div className="flex items-center justify-center gap-3 sm:gap-4">
      <VoteButton portrait={mind1.portrait} name={mind1.name} color="#2F3E46" onClick={() => onVote('mind1')} testId={`vote-mind1-round-${roundNum}`} />
      <button data-testid={`vote-draw-round-${roundNum}`} onClick={() => onVote('draw')}
        className="px-4 py-2.5 rounded-lg font-ui text-[10px] tracking-wider uppercase cursor-pointer transition-all flex items-center gap-1.5"
        style={{ background: '#F2EFE9', border: '1px solid #E6E1DB', color: '#8C8680' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#B8860B'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#B8860B'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#F2EFE9'; e.currentTarget.style.color = '#8C8680'; e.currentTarget.style.borderColor = '#E6E1DB'; }}>
        <Minus className="w-3 h-3" /> Draw
      </button>
      <VoteButton portrait={mind2.portrait} name={mind2.name} color="#8B2252" onClick={() => onVote('mind2')} testId={`vote-mind2-round-${roundNum}`} />
    </div>
  </motion.div>
);

const VoteButton = ({ portrait, name, color, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-ui text-[10px] tracking-wider cursor-pointer transition-all"
    style={{ background: 'white', border: `1.5px solid ${color}30`, color }}
    onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = color; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = color; e.currentTarget.style.borderColor = `${color}30`; }}>
    <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
      {portrait ? <img src={portrait} alt={name} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: `${color}20` }} />}
    </div>
    <ThumbsUp className="w-3 h-3" />
    <span className="hidden sm:inline truncate max-w-[80px]">{name}</span>
  </button>
);

/* ── Verdict Card ── */
const VerdictCard = ({ mind1, mind2, score1, score2, draws, winner, onClose }) => (
  <motion.div className="text-center py-6 mt-4"
    initial={{ opacity: 0, y: 25, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ type: 'spring', stiffness: 150, damping: 18 }}>
    <div className="inline-block px-8 sm:px-12 py-8 rounded-2xl" style={{ background: 'white', border: '2px solid #B8860B30', boxShadow: '0 20px 50px rgba(184,134,11,0.1)' }}>
      <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: '#B8860B' }} />
      <h3 className="font-display text-2xl mb-1" style={{ color: '#1A1512' }}>Debate Complete</h3>
      <p className="font-ui text-xs tracking-wider uppercase mb-4" style={{ color: '#B8860B' }}>{winner.label}</p>

      {/* Score summary */}
      <div className="flex items-center justify-center gap-6 mb-5">
        <div className="text-center">
          <div className="w-14 h-16 rounded-lg overflow-hidden shadow-md mx-auto mb-1" style={{ border: score1 > score2 ? '2px solid #B8860B' : '2px solid #E6E1DB' }}>
            {mind1.portrait && <img src={mind1.portrait} alt={mind1.name} className="w-full h-full object-cover" />}
          </div>
          <p className="font-display text-xl" style={{ color: '#1A1512' }}>{score1}</p>
          <p className="font-ui text-[9px] tracking-wider uppercase" style={{ color: '#8C8680' }}>{mind1.name}</p>
        </div>
        {draws > 0 && (
          <div className="text-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1" style={{ background: '#F2EFE9' }}>
              <Minus className="w-4 h-4" style={{ color: '#B8860B' }} />
            </div>
            <p className="font-display text-xl" style={{ color: '#B8860B' }}>{draws}</p>
            <p className="font-ui text-[9px] tracking-wider uppercase" style={{ color: '#8C8680' }}>Draws</p>
          </div>
        )}
        <div className="text-center">
          <div className="w-14 h-16 rounded-lg overflow-hidden shadow-md mx-auto mb-1" style={{ border: score2 > score1 ? '2px solid #B8860B' : '2px solid #E6E1DB' }}>
            {mind2.portrait && <img src={mind2.portrait} alt={mind2.name} className="w-full h-full object-cover" />}
          </div>
          <p className="font-display text-xl" style={{ color: '#1A1512' }}>{score2}</p>
          <p className="font-ui text-[9px] tracking-wider uppercase" style={{ color: '#8C8680' }}>{mind2.name}</p>
        </div>
      </div>

      <button data-testid="debate-done-btn" onClick={onClose}
        className="px-8 py-3 font-display text-xs tracking-[0.2em] uppercase cursor-pointer"
        style={{ background: '#1A1512', color: '#FDFCFB', boxShadow: '0 4px 12px rgba(26,21,18,0.15)' }}>
        Return to Hall
      </button>
    </div>
  </motion.div>
);

/* ── Helpers ── */
const DebaterBadge = ({ mind, label, score, align }) => (
  <div className={`flex items-center gap-2 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
    <div className="w-9 h-11 sm:w-10 sm:h-12 rounded-lg overflow-hidden shadow-sm flex-shrink-0" style={{ border: '2px solid #E6E1DB' }}>
      {mind.portrait ? <img src={mind.portrait} alt={mind.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: '#F2EFE9' }} />}
    </div>
    <div className={align === 'right' ? 'text-right' : ''}>
      <p className="font-ui text-[8px] sm:text-[9px] tracking-wider uppercase" style={{ color: '#8C8680' }}>{label}</p>
      <p className="font-display text-xs sm:text-sm" style={{ color: '#1A1512' }}>{mind.name}</p>
      <p className="font-display text-sm sm:text-base" style={{ color: '#B8860B' }}>{score}</p>
    </div>
  </div>
);

const SpeechCard = ({ mind, content, label, delay, color }) => (
  <motion.div className="rounded-xl overflow-hidden"
    style={{ background: 'white', border: '1px solid #E6E1DB', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.5 }}>
    <div className="flex items-center gap-2.5 px-4 py-2.5" style={{ borderBottom: '1px solid #F2EFE9', borderLeft: `3px solid ${color}` }}>
      <div className="w-7 h-9 rounded overflow-hidden flex-shrink-0">
        {mind.portrait ? <img src={mind.portrait} alt={mind.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: '#F2EFE9' }} />}
      </div>
      <div>
        <p className="font-display text-xs" style={{ color: '#1A1512' }}>{mind.name}</p>
        <p className="font-ui text-[8px] tracking-wider uppercase" style={{ color }}>{label}</p>
      </div>
    </div>
    <div className="px-4 sm:px-5 py-3">
      <p className="font-body text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#2A2520' }}>{content}</p>
    </div>
  </motion.div>
);

export default DebateStage;
